import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import api from '../../utils/api.js';
import { handleApiError } from '../../utils/errorHandler.js';
import { PASSWORD_RULES, isPasswordValid } from '../../utils/passwordValidation.js';
import TruncatedCell from '../../components/TruncatedCell.js';
import UserFormModal from './UserFormModal.js';
import { useAuth } from '../../contexts/AuthContext.js';
import SkeletonLoader from '../../components/SkeletonLoader.js';
import Pagination from '../../components/Pagination.js';
import usePaginatedFetch from '../../utils/usePaginatedFetch.js';

function UserListPage() {
    const [confirmModal, setConfirmModal] = useState({ open: false, type: '', user: null });
    const [createModal, setCreateModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [resetModal, setResetModal] = useState({ open: false, user: null });
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [inputName, setInputName] = useState('');
    const [inputEmail, setInputEmail] = useState('');
    const [resetPassword, setResetPassword] = useState('');
    const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetError, setResetError] = useState(null);
    const [searchName, setSearchName] = useState('');
    const [searchEmail, setSearchEmail] = useState('');
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const searchParams = useMemo(() => {
        const p = {};
        if (searchName) p.name = searchName;
        if (searchEmail) p.email = searchEmail;
        return p;
    }, [searchName, searchEmail]);

    const paged = usePaginatedFetch('/users/search/', searchParams, 20);

    useEffect(() => {
        if (currentUser && !currentUser.is_admin) {
            navigate('/');
        }
    }, [currentUser, navigate]);

    useEffect(() => {
        paged.fetch();
    }, [searchParams, paged.page]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (paged.forbidden) navigate('/');
    }, [paged.forbidden, navigate]);

    const handleSearchClick = () => {
        paged.setPage(1);
        setSearchName(inputName);
        setSearchEmail(inputEmail);
    };

    const handleClearSearch = () => {
        paged.setPage(1);
        setInputName('');
        setInputEmail('');
        setSearchName('');
        setSearchEmail('');
    };

    const handleOpenConfirm = (type, user) => {
        setConfirmModal({ open: true, type, user });
    };

    const handleCloseConfirm = () => {
        setConfirmModal({ open: false, type: '', user: null });
    };

    const handleConfirmAction = async () => {
        const { type, user } = confirmModal;
        if (!user) return;

        try {
            if (type === 'delete') {
                const response = await api.fetch(`/users/${user.id}/archive`, {
                    method: 'POST',
                });
                if (response && response.ok) paged.fetch();
                else alert('ユーザーの削除に失敗しました。');
            }
        } catch (err) {
            alert('エラーが発生しました。');
        } finally {
            handleCloseConfirm();
        }
    };

    const handleOpenCreateModal = () => {
        setEditingUser(null);
        setCreateModal(true);
    };

    const handleOpenEditModal = (user) => {
        setEditingUser(user);
        setCreateModal(true);
    };

    const handleModalSaved = () => {
        setCreateModal(false);
        setEditingUser(null);
        paged.fetch();
    };

    const handleModalClose = () => {
        setCreateModal(false);
        setEditingUser(null);
    };

    const handleOpenResetModal = (user) => {
        setResetModal({ open: true, user });
        setResetPassword('');
        setResetPasswordConfirm('');
        setResetError(null);
        setShowPassword(false);
        setShowPasswordConfirm(false);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!isPasswordValid(resetPassword)) {
            setResetError('パスワードは8文字以上で、英字と数字をそれぞれ1文字以上含めてください。');
            return;
        }
        if (resetPassword !== resetPasswordConfirm) {
            setResetError('パスワードが一致しません。');
            return;
        }

        setResetLoading(true);
        setResetError(null);
        try {
            const response = await api.fetch(`/users/${resetModal.user.id}/reset-password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ new_password: resetPassword }),
            });

            if (response && response.ok) {
                setResetModal({ open: false, user: null });
                alert('パスワードを更新しました。');
            } else {
                const errorMessage = await handleApiError(response, 'パスワードの更新に失敗しました。');
                setResetError(errorMessage);
            }
        } catch (err) {
            setResetError('通信エラーが発生しました。');
        } finally {
            setResetLoading(false);
        }
    };


    return (
        <div className="user-management-page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 className="page-title">ユーザー管理</h1>
                <button
                    className="modal-button-confirm"
                    style={{ margin: 0 }}
                    onClick={handleOpenCreateModal}
                >
                    + 新規ユーザー作成
                </button>
            </div>

            {/* ツールバーセクション */}
            <div className="toolbar-section">
                <div className="search-area">
                    <input
                        type="text"
                        placeholder="氏名..."
                        className="search-input"
                        value={inputName}
                        onChange={(e) => setInputName(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSearchClick();
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Email..."
                        className="search-input"
                        value={inputEmail}
                        onChange={(e) => setInputEmail(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSearchClick();
                        }}
                    />
                    <button className="search-button" onClick={handleSearchClick}>
                        検索
                    </button>
                    {(searchName || searchEmail) && (
                        <button
                            className="search-button"
                            style={{ backgroundColor: '#6c757d', marginLeft: '10px' }}
                            onClick={handleClearSearch}
                        >
                            クリア
                        </button>
                    )}
                    <button
                        className="btn-toolbar"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => navigate('/settings/users/archive')}
                    >
                        🗂 削除済みユーザー
                    </button>
                </div>
            </div>

            <div className="meeting-list-card">
                <h2 className="card-title">ユーザー一覧</h2>
                {paged.loading && paged.items.length === 0 && <SkeletonLoader variant="list" rows={5} />}
                {paged.error && <p className="error-message">エラー: {paged.error}</p>}
                {(!paged.loading || paged.items.length > 0) && !paged.error && (
                    <div className="table-responsive" style={{
                        opacity: paged.loading ? 0.6 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                        pointerEvents: paged.loading ? 'none' : 'auto'
                    }}>
                        <table>
                            <thead>
                                <tr>
                                    <th>氏名</th>
                                    <th>Email</th>
                                    <th>状況</th>
                                    <th>権限</th>
                                    <th>AI利用</th>
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.items.length > 0 ? (
                                    paged.items.map(user => (
                                        <tr key={user.id} onClick={() => handleOpenEditModal(user)}
                                            style={{ cursor: 'pointer', transition: 'background-color 0.15s' }}
                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f8ff'}
                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = ''}
                                        >
                                            <td><TruncatedCell text={user.user_name} /></td>
                                            <td><TruncatedCell text={user.user_email} maxLength={25} /></td>
                                            <td>
                                                <span
                                                    style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        backgroundColor: user.is_active ? '#e1f5fe' : '#ffebee',
                                                        color: user.is_active ? '#01579b' : '#c62828',
                                                    }}
                                                >
                                                    {user.is_active ? '有効' : '無効'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        backgroundColor: user.is_admin ? '#ede7f6' : '#f5f5f5',
                                                        color: user.is_admin ? '#512da8' : '#616161',
                                                    }}
                                                >
                                                    {user.is_admin ? '管理者' : '一般'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        backgroundColor: user.has_ai_access ? '#e8f5e9' : '#ffebee',
                                                        color: user.has_ai_access ? '#2e7d32' : '#c62828',
                                                    }}
                                                >
                                                    {user.has_ai_access ? '利用可' : '利用不可'}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => handleOpenResetModal(user)}
                                                        className="btn-row btn-row--ghost"
                                                    >
                                                        パスワードリセット
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenConfirm('delete', user)}
                                                        className="btn-row btn-row--danger"
                                                    >
                                                        削除
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="no-data">ユーザーは見つかりませんでした。</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {!paged.loading && !paged.error && paged.totalPages > 0 && (
                    <Pagination
                        currentPage={paged.page}
                        totalPages={paged.totalPages}
                        totalItems={paged.total}
                        perPage={paged.perPage}
                        onPageChange={paged.setPage}
                    />
                )}
            </div>

            {/* 確認モーダル */}
            {confirmModal.open && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3 className="modal-title">ユーザー削除 (アーカイブ)</h3>
                        <p style={{ margin: '20px 0', textAlign: 'center' }}>
                            {confirmModal.user.user_name} 様を削除しますか？この操作は取り消せません。
                        </p>
                        <div className="modal-actions">
                            <button className="modal-button-cancel" onClick={handleCloseConfirm}>キャンセル</button>
                            <button
                                className="modal-button-confirm"
                                style={{ backgroundColor: '#d32f2f' }}
                                onClick={handleConfirmAction}
                            >
                                削除する
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ユーザー作成/編集モーダル */}
            {createModal && (
                <UserFormModal
                    editingUser={editingUser}
                    onClose={handleModalClose}
                    onSaved={handleModalSaved}
                />
            )}

            {/* パスワードリセットモーダル */}
            {resetModal.open && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3 className="modal-title">パスワードリセット</h3>
                        <p style={{ marginBottom: '15px' }}>
                            {resetModal.user?.user_name} 様の新しいパスワードを入力してください。
                        </p>
                        <form onSubmit={handleResetPassword}>
                            <div className="auth-form-group">
                                <label className="auth-label">新しいパスワード *</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="auth-input"
                                        value={resetPassword}
                                        onChange={(e) => setResetPassword(e.target.value)}
                                        required
                                        placeholder="新しいパスワード"
                                        style={{ paddingRight: '40px' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '12px',
                                            color: '#666'
                                        }}
                                    >
                                        {showPassword ? '隠す' : '表示'}
                                    </button>
                                </div>
                                {resetPassword && (
                                    <div className="password-rules">
                                        {PASSWORD_RULES.map((rule, i) => (
                                            <small key={i} className={rule.test(resetPassword) ? 'rule-pass' : 'rule-fail'}>
                                                {rule.test(resetPassword) ? '✓' : '✗'} {rule.label}
                                            </small>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="auth-form-group">
                                <label className="auth-label">新しいパスワード（確認用） *</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPasswordConfirm ? 'text' : 'password'}
                                        className="auth-input"
                                        value={resetPasswordConfirm}
                                        onChange={(e) => setResetPasswordConfirm(e.target.value)}
                                        required
                                        placeholder="新しいパスワード（確認用）"
                                        style={{ paddingRight: '40px' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '12px',
                                            color: '#666'
                                        }}
                                    >
                                        {showPasswordConfirm ? '隠す' : '表示'}
                                    </button>
                                </div>
                            </div>
                            {resetError && (
                                <div className="auth-error">
                                    {resetError}
                                </div>
                            )}
                            <div className="modal-actions">
                                <button type="button" className="modal-button-cancel" onClick={() => setResetModal({ open: false, user: null })} disabled={resetLoading}>
                                    キャンセル
                                </button>
                                <button type="submit" className="modal-button-confirm" disabled={resetLoading || !isPasswordValid(resetPassword)}>
                                    {resetLoading ? '更新中...' : 'パスワードを更新'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserListPage;
